import { useMutation, useQuery } from "@tanstack/react-query";
import { signInWithCustomToken, signOut, UserCredential } from "firebase/auth";
import { auth } from "@/config/firebase";
import {
  verifySharedToken,
  getCustomToken,
  getUserInfo,
  IUserInfo,
  IToken,
} from "@/services/apis/authApi";
import { updateAuthTokenCookie, getCookie } from "@/utils/cookies";
import { COOKIES } from "@/constants";
import {
  redirectToLogin,
  shouldBounceToAccountsForMissingSharedCookie,
} from "@/helpers/redirection.helpers";
import type { SharedCookie } from "@/types/cookies";
import { IS_LOCAL_DEV } from "@/config/localDev";

interface UseAuthProps {
  onSuccess?: (userInfo: IUserInfo) => void;
  onError?: (error: unknown) => void;
}

interface VerifyTokenParams {
  token: string;
  user: IUserInfo;
}

/** Manages shared-cookie polling → token verification → Firebase sign-in → user info retrieval. */
export const useUserAuth = ({ onSuccess, onError }: UseAuthProps = {}) => {
  // Poll for shared cookie every 3 seconds
  const sharedCookieQuery = useQuery<SharedCookie | null>({
    queryKey: ["sharedCookie"],
    queryFn: () => {
      const cookieValue = getCookie<SharedCookie>(COOKIES.saras_global);
      if (
        shouldBounceToAccountsForMissingSharedCookie({
          pathname: window.location.pathname,
          search: window.location.search,
          hasSharedCookie: !!cookieValue,
          isLocalDev: IS_LOCAL_DEV,
        })
      ) {
        redirectToLogin();
      }
      return cookieValue;
    },
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
    gcTime: 0,
    retry: true,
    retryDelay: 1000,
    // No shared cookie to poll in local-dev mode.
    enabled: !IS_LOCAL_DEV,
  });
  const verifyTokenMutation = useMutation({
    mutationFn: async ({ token, user }: VerifyTokenParams) => {
      // Step 1: Verify token and get user info
      const verifyResponse: IToken = await verifySharedToken(token);

      if (!verifyResponse) {
        throw new Error("Invalid response from verify token endpoint");
      }

      // Step 2: Get firebase custom token
      const customTokenResponse = await getCustomToken(
        verifyResponse.uid,
        user.email || "",
        user.firstName || "",
        verifyResponse.roles || []
      );

      if (!customTokenResponse?.data) {
        throw new Error("Failed to get custom token");
      }

      // Step 3: Sign in with custom token
      const signinResponse: UserCredential = await signInWithCustomToken(
        auth,
        customTokenResponse.data
      );

      // Step 4: Get new ID token and update cookie
      const newToken = await signinResponse.user.getIdToken();
      updateAuthTokenCookie(newToken);

      if (!user.userId) {
        throw new Error("User ID is missing");
      }
      // Step 5: Get user info
      const userInfoResponse: IUserInfo = await getUserInfo(user.userId);

      if (!userInfoResponse) {
        throw new Error("Failed to fetch user info");
      }

      return userInfoResponse;
    },
    onSuccess: (data: IUserInfo) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error("Auth error:", error instanceof Error ? error.message : String(error));
      signOut(auth).catch((err: unknown) => console.error("Sign-out failed during auth error cleanup", err));
      onError?.(error);
    },
  });

  return {
    sharedCookieQuery,
    verifyToken: verifyTokenMutation.mutate,
    isVerifying: verifyTokenMutation.isPending,
    error: verifyTokenMutation.error,
  };
};
